import { CircleAlertIcon } from 'lucide-react';
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useModuleRegistry } from '@/shared/registry/context';

import { EmptyState } from '../empty-state/EmptyState';

import { SidePanel, SidePanelBody, SidePanelHeader } from './SidePanel';
import type { PanelEntry } from './url';
import { useSidePanel } from './useSidePanel';

interface RenderedPanel {
  entry: PanelEntry;
  depth: number;
  open: boolean;
}

class PanelErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[side-panel] render failed', error, info.componentStack);
  }
  override render() {
    return this.state.error ? this.props.fallback : this.props.children;
  }
}

function UnknownPanel({ type }: { type: string }) {
  const { t } = useTranslation('shared');
  return (
    <>
      <SidePanelHeader title={t('sidePanel.unavailable')} />
      <SidePanelBody>
        <EmptyState
          icon={CircleAlertIcon}
          title={t('sidePanel.unavailable')}
          description={t('sidePanel.unknownType', { type })}
        />
      </SidePanelBody>
    </>
  );
}

function PanelCrashed() {
  const { t } = useTranslation('shared');
  return (
    <>
      <SidePanelHeader title={t('errors.unknown')} />
      <SidePanelBody>
        <EmptyState
          icon={CircleAlertIcon}
          title={t('errors.renderFailed')}
          description={t('errors.renderFailedHint')}
        />
      </SidePanelBody>
    </>
  );
}

/**
 * Renders the URL panel stack (`?panel=type:id…`) with the components modules registered in `sidePanels`.
 * Mounted once by the app shell. Keeps closed panels mounted until their exit animation ends.
 */
export function SidePanelHost() {
  const { t } = useTranslation('shared');
  const registry = useModuleRegistry();
  const { pathname } = useLocation();
  const { stack, closeTo } = useSidePanel();
  const [previous, setPrevious] = useState(stack);
  const [closing, setClosing] = useState<RenderedPanel[]>([]);
  const isModal = (type: string) => registry.getSidePanel(type)?.modal?.(pathname) ?? true;
  // A non-modal panel keeps its place (and React identity) when another entity of its type replaces it: the content
  // swaps without the slide-out / slide-in of a new panel.
  const renderKey = (entry: PanelEntry, depth: number) =>
    isModal(entry.type) ? entry.key : `in-place:${depth}:${entry.type}`;

  // Derive "closing" panels while rendering (React's sanctioned pattern for state based on previous props).
  if (previous !== stack) {
    const current = new Set(stack.map((entry, depth) => renderKey(entry, depth)));
    const removed = previous
      .map((entry, depth) => ({ entry, depth, open: false }))
      .filter((p) => !current.has(renderKey(p.entry, p.depth)));
    setPrevious(stack);
    setClosing(removed);
  }

  // Mount at most one new panel per frame. Each modal panel hides everything outside itself from assistive tech
  // when it mounts, so mounting a deep-linked stack at once would leave the top panel hidden behind the others.
  const [revealed, setRevealed] = useState(0);
  if (revealed > stack.length) setRevealed(stack.length);
  const shown = Math.min(stack.length, revealed + 1);
  useEffect(() => {
    if (shown === revealed) return;
    const frame = requestAnimationFrame(() => setRevealed(shown));
    return () => cancelAnimationFrame(frame);
  }, [shown, revealed]);

  const openKeys = new Set(stack.map((entry, depth) => renderKey(entry, depth)));
  const panels: RenderedPanel[] = [
    ...stack.slice(0, shown).map((entry, depth) => ({ entry, depth, open: true })),
    ...closing.filter((c) => !openKeys.has(renderKey(c.entry, c.depth))),
  ].sort((a, b) => a.depth - b.depth);

  return panels.map(({ entry, depth, open }) => {
    const definition = registry.getSidePanel(entry.type);
    const PanelComponent = definition?.component;
    return (
      <SidePanel
        key={renderKey(entry, depth)}
        open={open}
        depth={depth}
        modal={isModal(entry.type)}
        size={registry.getSidePanelSize(entry.type) ?? 'lg'}
        shareable
        title={definition?.titleKey ? t(definition.titleKey) : t('sidePanel.title')}
        onOpenChange={(next) => {
          if (!next && open) closeTo(depth - 1);
        }}
        onExited={() =>
          setClosing((list) => list.filter((p) => renderKey(p.entry, p.depth) !== renderKey(entry, depth)))
        }
      >
        <PanelErrorBoundary fallback={<PanelCrashed />}>
          {PanelComponent ? <PanelComponent id={entry.id} /> : <UnknownPanel type={entry.type} />}
        </PanelErrorBoundary>
      </SidePanel>
    );
  });
}
